import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/providers/api-client';

// ============================================
// Types
// ============================================

export interface StaffMetrics {
  adminUserId: number;
  username: string;
  email: string;
  ordersProcessed: number;
  statusChanges: number;
  notesAdded: number;
  avgProcessingTimeMinutes: number | null;
  conversions: number;
  conversionRate: number;
}

export interface StaffDetailedMetrics extends StaffMetrics {
  ordersByStatus: Record<string, number>;
  activityByDay: Array<{ date: string; actions: number }>;
  recentActions: Array<{
    orderId: number;
    action: string;
    createdAt: string;
  }>;
}

export interface StaffMetricsSummary {
  metrics: StaffMetrics[];
  totals: {
    totalOrders: number;
    totalStatusChanges: number;
    totalConversions: number;
    avgConversionRate: number;
  };
  period: {
    type: string;
    startDate: string;
    endDate: string;
  };
}

export interface SalesReportItem {
  adminUserId: number;
  username: string;
  orderId: number;
  orderTotal: number;
  orderCurrency: string;
  statusName: string;
  action: string;
  actionDate: string;
}

export interface SalesReportData {
  report: SalesReportItem[];
  count: number;
  period: {
    type: string;
    startDate: string;
    endDate: string;
  };
}

export interface ProcessingTimeData {
  adminUserId: number;
  username: string;
  avgMinutes: number;
  medianMinutes: number;
  minMinutes: number;
  maxMinutes: number;
  samplesCount: number;
}

export interface ProcessingTimesData {
  data: ProcessingTimeData[];
  period: {
    type: string;
    startDate: string;
    endDate: string;
  };
}

export interface StaffDetailedMetricsData {
  metrics: StaffDetailedMetrics;
  period: {
    type: string;
    startDate: string;
    endDate: string;
  };
}

// ============================================
// API Functions
// ============================================

async function fetchStaffMetrics(
  period: string,
  startDate?: string,
  endDate?: string
): Promise<StaffMetricsSummary> {
  const params = new URLSearchParams({ period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}

  return await apiClient.get<StaffMetricsSummary>(
    `/admin/staff-analytics/metrics?${params}`
  );
}

async function fetchStaffDetailedMetrics(
  adminUserId: number,
  period: string,
  startDate?: string,
  endDate?: string
): Promise<StaffDetailedMetricsData> {
  const params = new URLSearchParams({ period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}

  return await apiClient.get<StaffDetailedMetricsData>(
    `/admin/staff-analytics/metrics/${adminUserId}?${params}`
  );
}

async function fetchSalesReport(
  period: string,
  startDate?: string,
  endDate?: string,
  adminUserId?: number
): Promise<SalesReportData> {
  const params = new URLSearchParams({ period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}
  if (adminUserId) {params.append('adminUserId', String(adminUserId));}

  return await apiClient.get<SalesReportData>(
    `/admin/staff-analytics/sales-report?${params}`
  );
}

async function fetchProcessingTimes(
  period: string,
  startDate?: string,
  endDate?: string
): Promise<ProcessingTimesData> {
  const params = new URLSearchParams({ period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}

  return await apiClient.get<ProcessingTimesData>(
    `/admin/staff-analytics/processing-times?${params}`
  );
}

// ============================================
// Hooks
// ============================================

/**
 * Хук для получения метрик всех сотрудников
 */
export function useStaffMetrics(
  period = 'month',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['staff-metrics', period, startDate, endDate],
    queryFn: () => fetchStaffMetrics(period, startDate, endDate),
    staleTime: 60 * 1000, // 1 минута
    refetchOnWindowFocus: false,
  });
}

/**
 * Хук для получения детальных метрик одного сотрудника
 */
export function useStaffDetailedMetrics(
  adminUserId: number,
  period = 'month',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['staff-detailed-metrics', adminUserId, period, startDate, endDate],
    queryFn: () => fetchStaffDetailedMetrics(adminUserId, period, startDate, endDate),
    staleTime: 60 * 1000, // 1 минута
    refetchOnWindowFocus: false,
    enabled: adminUserId > 0,
  });
}

/**
 * Хук для получения отчета по продажам
 */
export function useSalesReport(
  period = 'month',
  startDate?: string,
  endDate?: string,
  adminUserId?: number
) {
  return useQuery({
    queryKey: ['sales-report', period, startDate, endDate, adminUserId],
    queryFn: () => fetchSalesReport(period, startDate, endDate, adminUserId),
    staleTime: 60 * 1000, // 1 минута
    refetchOnWindowFocus: false,
  });
}

/**
 * Хук для получения времени обработки
 */
export function useProcessingTimes(
  period = 'month',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['processing-times', period, startDate, endDate],
    queryFn: () => fetchProcessingTimes(period, startDate, endDate),
    staleTime: 60 * 1000, // 1 минута
    refetchOnWindowFocus: false,
  });
}

/**
 * Хук для экспорта метрик в Excel
 */
export function useExportStaffMetrics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      period,
      startDate,
      endDate,
    }: {
      period: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const params = new URLSearchParams({ period });
      if (startDate) {params.append('startDate', startDate);}
      if (endDate) {params.append('endDate', endDate);}

      await apiClient.download(
        `/admin/staff-analytics/export/metrics?${params}`,
        `staff_metrics_${new Date().toISOString().split('T')[0]}.xlsx`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-metrics'] });
    },
  });
}

/**
 * Хук для экспорта отчета по продажам в Excel
 */
export function useExportSalesReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      period,
      startDate,
      endDate,
      adminUserId,
    }: {
      period: string;
      startDate?: string;
      endDate?: string;
      adminUserId?: number;
    }) => {
      const params = new URLSearchParams({ period });
      if (startDate) {params.append('startDate', startDate);}
      if (endDate) {params.append('endDate', endDate);}
      if (adminUserId) {params.append('adminUserId', String(adminUserId));}

      await apiClient.download(
        `/admin/staff-analytics/export/sales-report?${params}`,
        `sales_report_${new Date().toISOString().split('T')[0]}.xlsx`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-report'] });
    },
  });
}
